import { clsx } from 'clsx';
import { makeStyles } from '@mui/styles';
import { Button, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { PropsWithChildren } from 'react';
import {
  CreateButton,
  ListBase,
  Pagination,
  ListProps,
  useResourceContext,
  useResourceDefinitions,
} from 'react-admin';

import { ModalProvider } from '@providers';
import { FilterForm } from './FilterForm';
import { GridActions } from './GridActions';
import { FilterButton } from './FilterButton';
import { useCreateAccess, useRbacEdit } from '@hooks';
import { CustomExportButton } from './CustomExportButton';
import { VerticalAlignTopOutlined } from '@ant-design/icons';
import { OptionsButton } from '@components/GridList/OptionsButton';
import './filters.css';


type Props = ListProps & {
  filters: React.ReactNode[];
  className?: string;
  hideRemoveButton?: boolean;
  hideOptionsButton?: boolean;
  disableExportButton?: boolean;
  customCreate?: (arg?: any) => void;
  editComponent?: React.ReactElement | any;
  onExport?: (selectedIds: number[]) => Promise<void>;
  confirmRemoval?: boolean;
  confirmRestore?: boolean;
  summary?: React.ReactNode;
  mergeButton?: boolean;
  importButton?: (arg?: any) => void;
  bulkEditLockText?: string;
}

const CustomFilterButton = ({ filters, className }: {filters: any[], className?: string}) => {
  const moreFiltersTotal = filters.filter(f => !f.props?.alwaysOn).length;

  if (moreFiltersTotal === 0) {
    return null;
  }

  return (
    <FilterButton filters={filters} className={className} />
  );
};

export const GridList = (props: PropsWithChildren<Props>) => {
  const {
    filters, filter, className, hideRemoveButton, hideOptionsButton, summary, mergeButton,
    disableExportButton, customCreate, editComponent, confirmRemoval, confirmRestore, importButton,
    bulkEditLockText,
  } = props;
  const children = props.children as any;
  const cls = useStyles();
  const resource = useResourceContext();
  const resourcesByKey: any = useResourceDefinitions();
  const hasEditAccess = useRbacEdit();
  const hasCreateAccess = useCreateAccess(resource);

  const hasCreatePage = resourcesByKey && resourcesByKey[resource]
    && resourcesByKey[resource].hasCreate;
  const showCreateButton = hasCreatePage && hasCreateAccess && !customCreate;

  return (
    <div className={clsx(className, 'RaList-main')}>
      <ListBase resource={resource} exporter={props.exporter} filter={filter}
        perPage={props.perPage} sort={props.sort} debounce={props.debounce}>
        <Stack
          direction="row"
          className={clsx('MuiToolbar-root', 'RaFilter-form', 'css-RaTopToolbar-root', cls.header)}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            className={cls.filters}
          >
            <FilterForm filters={filters} />
          </Stack>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            className={cls.buttons}
          >
            {!hideOptionsButton && <OptionsButton />}
            <CustomFilterButton filters={filters} className={cls.filtersBtn} />
            {showCreateButton && (
              <CreateButton />
            )}
            {customCreate && (
              <Button variant="outlined"
                onClick={customCreate}
                startIcon={<AddIcon />}
                style={{ lineHeight: 1.5 }}>
                Create
              </Button>
            )}
            {importButton && (
              <Button variant="outlined"
                onClick={importButton}
                startIcon={<VerticalAlignTopOutlined />}
                style={{ lineHeight: 1.5 }}>
                Import
              </Button>
            )}
            <CustomExportButton
              exporter={props.exporter as any}
              onExport={props.onExport}
              columns={children?.props?.children || []}
              maxResults={999999999999}
              disabled={disableExportButton}
            />
          </Stack>
        </Stack>
        <div className={clsx('list-page', cls.listPage)}>
          <ModalProvider>
            <GridActions
              editComponent={hasEditAccess && editComponent ? editComponent : undefined}
              hideRemoveButton={hideRemoveButton}
              hasEditAccess={hasEditAccess}
              confirmRemoval={confirmRemoval}
              confirmRestore={confirmRestore}
              summary={summary}
              mergeButton={mergeButton}
              lockText={bulkEditLockText}
            />
          </ModalProvider>
          {children}
        </div>
        <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
      </ListBase>
    </div>
  );
};

const useStyles = makeStyles({
  header: {
    padding: '10px 0 0',
    maxWidth: 'calc(100vw - 290px)',
    justifyContent: 'space-between',
  },
  filters: {
    flex: '0 1 100%',
  },
  buttons: {
    paddingTop: 19,
    '& > *': {
      marginLeft: 15,
    },
  },
  filtersBtn: {},
  listPage: {
    position: 'relative',
  },
});
